import { useLayoutEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Typography from "@/Utility/Typography";
import FloatingTooltip from "@/Utility/FloatingTooltip/FloatingTooltip";
import WorkItemCopyLinkButton from "@/Pages/Issues/WorkItemCopyLinkButton";
import { copyWorkItemDetailLink } from "@/utils/workItemDetailUrl";
import { showSuccess, showError } from "@/utils/toast";
import {
  getIssueListPriorityBadgeStyle,
  getIssueListPriorityBorderStyle,
  getIssueListPriorityIcon,
  formatWorkItemProjectClientLabel,
  getWorkItemClientProjectParts,
} from "@/Pages/Issues/issueMappers";

const VALID_PROJECT_TABS = new Set(["dashboard", "issues", "boards", "backlog", "settings"]);

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

function resolveProjectTabForLink(search) {
  const tab = new URLSearchParams(search ?? "").get("tab");
  if (tab && VALID_PROJECT_TABS.has(tab)) return tab;
  return "issues";
}

const TRUNCATE_LINE = "block min-w-0 w-full max-w-full truncate";

function TruncatedWithTooltip({ text, children, tooltipText, className = "" }) {
  const ref = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const label = tooltipText ?? (text != null ? String(text) : "");

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      setIsOverflowing(el.scrollWidth > el.clientWidth + 1);
    };

    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(el);
    window.addEventListener("resize", measure);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [label, children]);

  const line = (
    <span ref={ref} className={`${TRUNCATE_LINE} ${className}`.trim()}>
      {children ?? label}
    </span>
  );

  if ((!label && !children) || !isOverflowing) return line;

  return (
    <FloatingTooltip label={label} anchorClassName="block min-w-0 w-full max-w-full">
      {line}
    </FloatingTooltip>
  );
}

const CARD_SIZE_CLASSES = {
  root: "flex w-full min-w-0 max-w-full flex-col overflow-hidden min-h-[var(--kanban-card-min-height,5.5rem)] p-3 mb-3 box-border",
  key: "text-xs leading-tight",
  title: "text-sm leading-snug",
  footer: "!text-[11px]",
  avatar: "w-6 h-6 shrink-0 !text-[10px]",
  assignee: "!text-[11px]",
  priority: "!text-[11px] px-2 py-0.5",
  footerGap: "mt-3 shrink-0",
};

const IssueCard = ({
  issue,
  projectSlug,
  dragDisabled = false,
  dragPreview = false,
  onOpenWorkItemDetail,
  /** When true (global board), show project name under the key for cross-project context. */
  showProjectName = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const projectIdForUrl = projectSlug ?? issue?.projectId ?? issue?.project?.id ?? null;
  const projectLabel = showProjectName ? formatWorkItemProjectClientLabel(issue) : "";
  const { clientName, projectName } = showProjectName
    ? getWorkItemClientProjectParts(issue)
    : { clientName: "", projectName: "" };

  const handleCopyWorkItemLink = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!projectIdForUrl || !issue?.id) {
        showError("Cannot copy link: missing project or work item.");
        return;
      }
      try {
        await copyWorkItemDetailLink({
          projectSlug: projectIdForUrl,
          workItemId: issue.id,
        });
        showSuccess("Link copied to clipboard.");
      } catch {
        showError("Could not copy link. Check browser permissions.");
      }
    },
    [projectIdForUrl, issue?.id]
  );

  const goToIssueDetail = () => {
    if (projectIdForUrl && onOpenWorkItemDetail) {
      onOpenWorkItemDetail(issue.id, projectIdForUrl);
      return;
    }
    if (projectIdForUrl) {
      const tab = resolveProjectTabForLink(location.search);
      navigate(`/projects/${projectIdForUrl}?tab=${encodeURIComponent(tab)}`);
      return;
    }
    navigate(`/create-issue?id=${issue.id}`);
  };

  const interactive = !dragPreview;
  const size = CARD_SIZE_CLASSES;

  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? goToIssueDetail : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                goToIssueDetail();
              }
            }
          : undefined
      }
      className={`${size.root} min-w-0 overflow-hidden bg-white rounded-md shadow-sm border border-gray-200 border-l-4
          ${
            dragDisabled || dragPreview
              ? "cursor-default"
              : "cursor-grab active:cursor-grabbing hover:shadow-md"
          } transition`}
      style={getIssueListPriorityBorderStyle(issue.priority)}
    >
      <div className="flex items-start justify-between gap-1 min-w-0">
        <div className="min-w-0 flex-1 overflow-hidden">
          <TruncatedWithTooltip
            text={issue.issueKey}
            className={`${size.key} font-semibold text-gray-500 hover:text-blue-600 hover:underline`}
          />
        </div>
        {interactive && projectIdForUrl && issue?.id ? (
          <div className="shrink-0" onPointerDown={(e) => e.stopPropagation()}>
            <WorkItemCopyLinkButton onClick={handleCopyWorkItemLink} />
          </div>
        ) : null}
      </div>

      {projectLabel ? (
        <div className="mt-0.5 min-w-0 w-full max-w-full overflow-hidden">
          <TruncatedWithTooltip
            tooltipText={projectLabel}
            className="flex min-w-0 items-baseline gap-1 text-[11px]"
          >
            {clientName ? (
              <span className="truncate font-bold text-indigo-700" title={`Client: ${clientName}`}>
                {clientName}
              </span>
            ) : null}
            {clientName && projectName ? (
              <span aria-hidden="true" className="shrink-0 font-normal text-gray-600">
                -
              </span>
            ) : null}
            {projectName ? (
              <span className="truncate font-bold text-slate-700" title={`Project: ${projectName}`}>
                {projectName}
              </span>
            ) : null}
          </TruncatedWithTooltip>
        </div>
      ) : null}

      <div className="mt-1 min-w-0 w-full max-w-full overflow-hidden">
        <TruncatedWithTooltip
          text={issue.title}
          className={`${size.title} font-medium text-gray-800`}
        />
      </div>

      <div
        className={`flex justify-between items-center gap-2 min-w-0 ${size.footerGap} text-xs text-gray-600`}
      >
        <Typography
          variant="span2"
          className={`flex items-center gap-2 min-w-0 flex-1 ${size.footer}`}
          component="span"
        >
          <Typography
            variant="span2"
            className={`${size.avatar} flex items-center justify-center font-semibold text-white`}
            style={{
              backgroundColor: "var(--avatarKanban-bg-color)",
              borderRadius: "var(--radius-full)",
            }}
          >
            {issue.assignee ? getInitials(issue.assignee) : "U"}
          </Typography>

          <Typography
            variant="span2"
            className={`truncate ${size.assignee}`}
            title={issue.assignee || "Unassigned"}
          >
            {issue.assignee || "Unassigned"}
          </Typography>
        </Typography>
        <Typography
          variant="span2"
          className={`inline-flex items-center gap-1 rounded font-medium border max-w-[min(100%,11rem)] min-w-0 shrink ${size.priority}`}
          component="span"
          style={getIssueListPriorityBadgeStyle(issue.priority)}
          title={issue.priority || "N/A"}
        >
          {getIssueListPriorityIcon(issue.priority)}
          <span className="truncate">{issue.priority || "N/A"}</span>
        </Typography>
      </div>
    </div>
  );
};

export default IssueCard;
